import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {userTaskGetAll} from '../../../redux/slices/usertask.slice'
import {basicLookup} from '../../../redux/slices/lookup.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

import {pagination} from '../../../helpers'

const selectUserTaskData = (state) => ({
  userTask: state.userTask.userTask,
  loading: state.userTask.loading,
})

const selectLookupData = (state) => ({
  UserTaskStatus: state.lookup.UserTaskStatus,
})

function List() {
  const intl = useIntl()
  const {userTask, loading} = useSelector(selectUserTaskData)
  const {UserTaskStatus} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState(pagination)

  useEffect(() => {
    if (params) dispatch(userTaskGetAll(params))
  }, [params, dispatch])

  useEffect(() => {
    dispatch(basicLookup('UserTaskStatus'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams(values)
  }, [])

  const Columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'USERTASK.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => <CustomCellDouble item={props} double='taskPriorityName' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'USERTASK.SEARCH.USERNAMESURNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'userNameSurname',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'USERTASK.SEARCH.REQUESTEDUSERNAMESURNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'requestedUserNameSurname',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'USERTASK.SEARCH.TASKDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'taskDate',
        Cell: ({...props}) => <CustomCell item={props} date='short' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'USERTASK.SEARCH.USERTASKSTATUSNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'userTaskStatusName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'USERTASK.SEARCH.COMPLETEDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'completeDate',
        Cell: ({...props}) => <CustomCell item={props} date='short' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'USERTASK.SEARCH.USERTASKSTATUSNAME',
            data: UserTaskStatus,
            name: 'userTaskStatusId',
          },
          {
            title: 'USERTASK.SEARCH.STARTDATE',
            type: 'date',
            name: 'startDate',
          },
          {
            title: 'USERTASK.SEARCH.ENDDATE',
            type: 'date',
            name: 'endDate',
          },
        ],
      },
    ],
    [UserTaskStatus]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.USERTASK'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages='USERTASK'
        />
        <KTCardBody>
          {!userTask && <ListLoading />}
          {userTask && (
            <Table items={userTask} cols={Columns} handlePagination={handlePagination} paddless />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
